import { Fragment, useCallback, useState } from "react";
import { TableActionLoader, TableRowLoader } from "components/loaders";
import {
  CusomerDefaultFilters,
  CustomerDefaultMultiFilter,
} from "content/DefaultFilter";
import { CustomerTableMenuOptions } from "content/Dropdown";
import { CancelSubscriptionModal } from "components/modals";
import { CustomerTableColumns } from "content/TableColumns";
import { sortOptions } from "content/SortDropdown";
import * as Components from "components";
import { debounce, formatPhoneNumberUS, formatThousand } from "utils";
import useFetch from "hooks/useFetch";
import * as Icons from "assets/svg";
import { Space } from "antd";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

const AllCustomers = () => {
  const [checkedTableMenuColumn, setCheckedTableMenuColumn] =
    useState(CustomerTableColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isCancelSubsModalOpen, setIsCancelSubsModalOpen] = useState(false);
  const [filter, setFilter] = useState(CusomerDefaultFilters);
  const [search, setSearch] = useState("");
  const [selectedLandlordId, setSelectedLandlordId] = useState("");
  const [isRerender, setIsRerender] = useState(false);
  const { data, loading } = useFetch(`/admin/landlords`, filter, [
    filter,
    isRerender,
  ]);
  const navigate = useNavigate();

  // Memoize the debouncedSearch function using useCallback
  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilter("search", value);
    }, 500),
    []
  );

  // Handle Search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleFilter = (name, value) => {
    if (name === "filter") {
      setFilter((prev) => ({
        ...prev,
        ...value,
        page: 1,
      }));
    } else if (name === "pagination") {
      setFilter((prev) => ({
        ...prev,
        page: value.current,
        limit: value.pageSize,
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: value,
        page: 1,
      }));
    }
    setSelectedRowKeys([]);
  };

  const handleFilterChange = (selectedFilters) => {
    const combinedFilters = Object.values(selectedFilters).flat();

    const formatedFilters = combinedFilters.reduce(
      (acc, item) => {
        if (
          item === "Trial" ||
          item === "Basic" ||
          item === "Pro" ||
          item === "Premium"
        ) {
          acc.plan = acc.plan ? `${acc.plan},${item}` : item;
        } else if (item === "monthly" || item === "yearly") {
          acc.billingCycle = acc.billingCycle
            ? `${acc.billingCycle},${item}`
            : item;
        } else if (
          item === "active" ||
          item === "canceled" ||
          item === "past_due" ||
          item === "incomplete" ||
          item === "incomplete_expired"
        ) {
          acc.status = acc.status ? `${acc.status},${item}` : item;
        } else if (item.fromDate || item.toDate) {
          acc.fromDate = item.fromDate;
          acc.toDate = item.toDate;
        }

        return acc;
      },
      { plan: "", billingCycle: "", status: "", fromDate: "", toDate: "" }
    );
    // update filters
    handleFilter("filter", formatedFilters);
  };

  const handleMenuClick = (key, record) => {
    if (key === "cancelSubscription") {
      setIsCancelSubsModalOpen(true);
      setSelectedLandlordId(record._id);
    } else if (key === "view") {
      // navigate(`/customers/${record._id}`);
    } else if (key === "changePlan") {
      toast.info("Comming Soon!");
    }
  };

  const onRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { firstName, lastName } = record;
        return (
          <div
            // onClick={() => navigate(`/customers/${record._id}`)}
            className="cursor-pointer"
          >{`${firstName} ${lastName}`}</div>
        );
      },
    },
    {
      title: "CONTACT INFORMATION",
      dataIndex: "contactInfo",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { phone, email } = record;
        return (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <img
                src={Icons.MobilePhoneIcon}
                alt=""
                className="size-5 shrink-0"
              />
              <span>{formatPhoneNumberUS(phone)}</span>{" "}
            </div>
            <div className="flex items-center gap-3">
              <img src={Icons.EmailIcon} alt="" className="size-5 shrink-0" />
              <span>{email}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "SUBSCRIPTION",
      dataIndex: "subscription",
      loader: <TableRowLoader />,
      render: (_, record) => {
        if (record.plan.name === "Basic") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-alpha/10">
              <div className="text-alpha">Basic</div>
            </div>
          );
        } else if (record.plan.name === "Pro") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-secondary/10">
              <div className="text-secondary">Pro</div>
            </div>
          );
        } else if (record.plan.name === "Premium") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-primary/10">
              <div className="text-primary">Premium</div>
            </div>
          );
        }
        return (
          <div className="w-fit px-3 py-1 rounded-full bg-red-50">
            <div className="text-red-500">Trial</div>
          </div>
        );
      },
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      loader: <TableRowLoader />,
      render: (_, record) => {
        return record.plan.name && record.plan.name !== "Trial" ? (
          <div>
            <span>${formatThousand(record.plan.amount)}</span> /{" "}
            <span>
              Per {record.plan.billingCycle === "yearly" ? "Year" : "Monthly"}
            </span>
          </div>
        ) : (
          <div>--</div>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "status",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const status = record.plan.status;

        switch (status) {
          case "expired":
            return (
              <div>
                <div className="text-red-500">Expired</div>
                <button className="text-xs font-semibold text-secondary">
                  Renew Now
                </button>
              </div>
            );

          case "canceled":
            return (
              <div>
                <div className="text-orange-500">Cancelled</div>
                <button className="text-xs font-semibold text-secondary">
                  Subscribe Again
                </button>
              </div>
            );

          case "inactive":
            return (
              <div>
                <div className="text-gray-500">Inactive</div>
                <button className="text-xs font-semibold text-secondary">
                  Activate
                </button>
              </div>
            );

          case "trial":
            // Calculate days remaining if trial expiry date is available
            let trialMessage;

            if (record.plan.trialExpiryDate) {
              const expiryDate = new Date(record.plan.trialExpiryDate);
              const today = new Date();
              const daysRemaining = Math.ceil(
                (expiryDate - today) / (1000 * 60 * 60 * 24)
              );

              if (daysRemaining > 0) {
                trialMessage = (
                  <span className="text-blue-500">
                    Trial ({daysRemaining} days left)
                  </span>
                );
              } else {
                trialMessage = (
                  <span className="text-red-500">Trial Expired</span>
                );
              }
            } else {
              trialMessage = <span className="text-blue-500">Trial</span>;
            }

            return (
              <div>
                <div>{trialMessage}</div>
                <button className="text-xs font-semibold text-secondary">
                  Upgrade
                </button>
              </div>
            );

          case "past_due":
            return (
              <div>
                <div className="text-red-500">Payment Overdue</div>
                <button className="text-xs font-semibold text-secondary">
                  Pay Now
                </button>
              </div>
            );

          case "active":
            const isCancelScheduled = !!record?.subscriptionCancellationDate;
            return (
              <div>
                <div className="text-green-500">Active</div>
                {isCancelScheduled && (
                  <div className="text-red-500 w-[155px]">
                    cancellation is scheduled on{" "}
                    {new Date(
                      record?.subscriptionCancellationDate
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            );

          case "incomplete":
            return (
              <div>
                <div className="text-yellow-500">Incomplete</div>
                <button className="text-xs font-semibold text-secondary">
                  Complete Setup
                </button>
              </div>
            );

          case "incomplete_expired":
            return (
              <div>
                <div className="text-red-500">Setup Expired</div>
                <button className="text-xs font-semibold text-secondary">
                  Try Again
                </button>
              </div>
            );

          default:
            return (
              <div>
                <div className="text-gray-500">Unknown</div>
              </div>
            );
        }
      },
    },
    {
      title: "",
      dataIndex: "action",
      loader: <TableActionLoader />,
      render: (_, record) => {
        const status = record.plan.status;
        const plan = record.plan.name;
        const isCancelScheduled = !!record?.subscriptionCancellationDate;

        return (
          <Space size="middle" className="flex justify-center">
            <Components.DropDownMenu
              menuItems={
                plan !== "Trial" &&
                (status === "active" || status === "past_due") &&
                !isCancelScheduled
                  ? CustomerTableMenuOptions
                  : CustomerTableMenuOptions.filter(
                      (option) => option.key !== "cancelSubscription"
                    )
              }
              onMenuClick={(actionKey) => handleMenuClick(actionKey, record)}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Fragment>
      <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-[#181818]">
        <div className="w-full flex items-center px-5 py-3">
          <div className="w-full flex gap-5">
            <div className="flex gap-5 items-center w-full xl:w-2/3">
              <div id="step3" className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search User by Name, Email or Phone"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <Components.SortDropdown
                sortOptions={sortOptions}
                propertyType={true}
                onSortChange={(value) => handleFilter("sortBy", value)}
              />
              <Components.MultiCategoryFilter
                categories={CustomerDefaultMultiFilter}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex items-center justify-end xl:w-1/2">
              <Components.TableCustomizeColumnMenu
                setCheckedTableMenuColumn={setCheckedTableMenuColumn}
                checkedTableMenuColumn={checkedTableMenuColumn}
              />
            </div>
          </div>
        </div>

        <div id="step4">
          <Components.CustomTable
            columns={columns.filter(
              (column) =>
                column.dataIndex === "action" ||
                checkedTableMenuColumn.some(
                  (col) => col.dataIndex === column.dataIndex && col.visible
                )
            )}
            dataSource={data?.landlords}
            pagination={true}
            rowSelection={rowSelection}
            isLoading={loading}
            metaData={data?.metaData}
            setPagination={(pagination) =>
              handleFilter("pagination", pagination)
            }
            pageLimit={filter.limit}
          />
        </div>
      </div>
      <CancelSubscriptionModal
        landlordId={selectedLandlordId}
        isOpen={isCancelSubsModalOpen}
        onClose={() => {
          setIsCancelSubsModalOpen(false);
          setIsRerender((prev) => !prev);
        }}
      />
    </Fragment>
  );
};

export default AllCustomers;
